/* eslint-disable no-console */
import { useContext } from 'react';
import { Pay } from '@gov-mo/mpaas-js-bridge';
import type { PayConfig } from '@gov-mo/mpaas-js-bridge/es/module/pay';
import { usePersistFn } from 'ahooks';
import { useDispatch, useSelector, history, useIntl } from 'umi';
import type { FormModelsState, StepModelsState } from 'umi';
import { securityLevel, stepInfo, StepType } from '@/utils/ServiceConf';
import * as queryString from 'query-string';
import { isMobile } from '@/utils';
import { isPaySuccess } from '../../services/serviceStepApi';
import StepContext from './StepContext';

function useHandleStep(currentStep: { stepType: StepType; stepName: string }) {
  const intl = useIntl();

  const context = useContext(StepContext);
  const dispatch = useDispatch();

  const { step } = useSelector((x: { stepModels: StepModelsState }) => x.stepModels);
  const { payData } = useSelector((x: { formModels: FormModelsState }) => x.formModels);

  // 更新/初始化models
  const updataModels = usePersistFn((payload) => {
    dispatch({
      type: 'stepModels/update',
      payload,
    });
  });

  /**
   * 最後一步提交操作
   * status: 0
   */
  const _handleSaveOrSubmit = usePersistFn<() => void>(() => {
    // 跳轉到辦件成功提示頁面
    history.push(
      queryString.stringifyUrl({
        url: '/complete-submission',
        query: {
          workNo: '123456789',
        },
      }),
    );
  });

  /**
   * 下一步跳转
   */
  const _handleNext = () => {
    // 最后一步，提交，后续也需根据需求区分是否有提交不在最后一步的情况情况
    if (step === stepInfo.length) {
      _handleSaveOrSubmit();
    } else {
      // 更新當前步驟
      updataModels({ step: step + 1 });
    }
  };

  /**
   * 下一步业务封装
   */
  const handleNextStep = usePersistFn(async () => {
    // CASE1: 身份識別步驟：且未识别成功过，则调起身份识别
    if (currentStep.stepType === StepType.faceRecognition && !context.isAuth) {
      context.identityRef.handleAuth({ authLevel: securityLevel.HIGH });
      return;
    }
    // CASE2: 表单类步骤触发校验并保存
    if (currentStep.stepType === StepType.form) {
      const formValidateFields = context.formRenderInstance?.validateFields;
      const formValue = await formValidateFields?.();
      // 将数据保存到model中
      dispatch({
        type: 'formModels/update',
        formData: formValue,
      });
      // 跳转到下一步
      _handleNext();
      return;
    }

    // CASE3: 支付步驟&且未完成支付，且在账单页
    if (currentStep.stepType === StepType.payment && !context.isPayed && !payData) {
      // 向后端查询支付状态，确认确实还没进行支付成功，避免重复支付
      const res = await isPaySuccess('xxxxx');
      if (!res.data) {
        // 組裝收銀台參數
        const paymentOrder: PayConfig = {
          title: intl.formatMessage({ id: 'Demo事项' }),
          currency: 'MOP',
          amount: '100',
          billId: 'xxxx',
          terminalType: 'MOBILE',
          tarUrl: window.location.origin,
          // 各事项根据后端接口是否满足自行定制化修改
          orderServer: `${API_HOST}/payment/order`,
          stateServer: `${API_HOST}/payment/bill/success`,
          payChannels: ['BOCPAY', 'MPGS', 'BOCCARDPAY', 'MPAY'],
          redirectRelUri: `${window.routerBase.replace('/', '')}pay-passed`,
        };
        if (isMobile()) {
          // 移動端支付
          await Pay.start(paymentOrder);
        } else {
          // 数据存储起来，供页面使用
          dispatch({ type: 'formModels/update', payData: { ...paymentOrder, terminalType: 'PC' } });
        }
      } else {
        context.isPayed = true;
        _handleNext();
      }
      return;
    }

    // CASE4: 支付步驟&且未完成支付，且在支付渠道选择页
    if (currentStep.stepType === StepType.payment && !context.isPayed && payData) {
      context.moPayRef.goPay();
      return;
    }

    // CASE5: 其他直接跳转到下一步
    _handleNext();
  });

  return handleNextStep;
}

export default useHandleStep;
